import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { useWorkspaceStore } from '@/store/workspaceStore';
import { useSocketEvents } from '@/hooks/useSocket';
import { useIdleTimer } from '@/hooks/useIdleTimer';
import { getSocket } from '@/lib/socket';
// import { useAppUpdate } from '@/hooks/useAppUpdate';
import LoginPage from '@/pages/auth/LoginPage';
import RegisterPage from '@/pages/auth/RegisterPage';
import ConfirmEmailPage from '@/pages/auth/ConfirmEmailPage';
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/auth/ResetPasswordPage';
import RestoreAccountPage from '@/pages/auth/RestoreAccountPage';
import ChatPage from '@/pages/chat/ChatPage';
import InvitePage from '@/pages/chat/InvitePage';
import AdminPage from '@/pages/admin/AdminPage';
import PrivacyPolicyPage from '@/pages/legal/PrivacyPolicyPage';
import TermsPage from '@/pages/legal/TermsPage';
import ClaudeDiaryPage from '@/pages/claude/ClaudeDiaryPage';
import ClaudeWritePage from '@/pages/claude/ClaudeWritePage';
import EmbedChatPage from '@/pages/embed/EmbedChatPage';
import TikTokPromoAdminPage from '@/pages/TikTokPromoAdminPage';
import ProtectedRoute from '@/components/layout/ProtectedRoute';
import AdminRoute from '@/components/layout/AdminRoute';
import CallOverlay from '@/components/call/CallOverlay';
import WorkspaceProvider from '@/components/workspace/WorkspaceProvider';
import WorkspaceArchivedScreen from '@/components/workspace/WorkspaceArchivedScreen';
import WorkspaceNotFoundScreen from '@/components/workspace/WorkspaceNotFoundScreen';
import SWUpdateBanner from '@/components/ui/SWUpdateBanner';
import SocketStatusIndicator from '@/components/ui/SocketStatusIndicator';
import { SwipaLoader } from '@/icons';

function SocketInit() {
  useSocketEvents();
  // v5.3 — Auto-AWAY. После 5 минут бездействия emit'им status:set('AWAY').
  // На любую активность — обратно ONLINE. visibilitychange:hidden тоже AWAY.
  // Если юзер manual выставил DND — AutoAway его не перебьёт (DND держит
  // юзер целенаправленно). Здесь просто заводим callbacks; статус-стор UI
  // не трогаем (server бродкастит status update обратно через user:status).
  useIdleTimer({
    idleMs: 5 * 60_000,
    onIdle: () => {
      const s = getSocket();
      if (s.connected) s.emit('status:set', { status: 'AWAY' });
    },
    onActive: () => {
      const s = getSocket();
      if (s.connected) s.emit('status:set', { status: 'ONLINE' });
    },
  });
  return null;
}

// function AppUpdateInit() {
//   useAppUpdate();
//   return null;
// }

export default function App() {
  const { initialize, isLoading, isAuthenticated } = useAuthStore();
  // Phase 12.1 — workspace context for branded subdomains. Provider triggers
  // detect() on mount; we read error here to short-circuit the entire app
  // tree when on a dead/unknown subdomain.
  const wsError = useWorkspaceStore((s) => s.error);
  const wsDetected = useWorkspaceStore((s) => s.detected);

  useEffect(() => {
    initialize();
  }, [initialize]);

  if (isLoading) {
    // Brand splash — two/three chevron wave on burgundy gradient, matches
    // the sWipa design bundle's splash-screen mock (see icons/SwipaLoader.tsx).
    return <SwipaLoader mode="splash" />;
  }

  // Workspace blocker: if subdomain workspace is archived/missing, replace the
  // whole UI. WorkspaceProvider is mounted around routes anyway so it can
  // re-detect on navigation (rare).
  if (wsDetected && wsError) {
    return (
      <WorkspaceProvider>
        {wsError.code === 'WORKSPACE_ARCHIVED' ? <WorkspaceArchivedScreen /> : <WorkspaceNotFoundScreen />}
      </WorkspaceProvider>
    );
  }

  return (
    <WorkspaceProvider>
      {isAuthenticated && <SocketInit />}
      {isAuthenticated && <CallOverlay />}
      {/* v5.7.7 — SW update banner. Mount-ed regardless of auth, чтобы юзер
          мог получить новый bundle ещё на login-странице (до того как
          устаревший SDK попробует JWT refresh и т.п.). */}
      <SWUpdateBanner />
      {/* v5.7.7 P3+P4 — socket health: auto-refresh+reconnect при auth-error,
          UI banner "соединение потеряно" пока socket down. */}
      {isAuthenticated && <SocketStatusIndicator />}
      {/* {isAuthenticated && <AppUpdateInit />} */}
      <Routes>
        {/* Public */}
        <Route path="/auth/login" element={<LoginPage />} />
        <Route path="/auth/register" element={<RegisterPage />} />
        <Route path="/auth/confirm-email" element={<ConfirmEmailPage />} />
        <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
        <Route path="/auth/restore-account" element={<RestoreAccountPage />} />

        {/* Legal (public) */}
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsPage />} />

        {/* Claude AI — публичный дневник + контактная форма */}
        <Route path="/claude" element={<ClaudeDiaryPage />} />
        <Route path="/claude/write" element={<ClaudeWritePage />} />

        {/* Embed (public): минимальное окно чата для iframe-интеграций.
            Auth по token=<accessToken> в query. Не требует ProtectedRoute. */}
        <Route path="/embed/chat" element={<EmbedChatPage />} />

        {/* Invite (protected) */}
        <Route element={<ProtectedRoute />}>
          <Route path="/invite/:code" element={<InvitePage />} />
        </Route>

        {/* Protected */}
        <Route element={<ProtectedRoute />}>
          <Route index element={<ChatPage />} />
          <Route element={<AdminRoute />}>
            <Route path="/tt" element={<TikTokPromoAdminPage />} />
                            <Route path="/admin/*" element={<AdminPage />} />
          </Route>
          <Route path="/*" element={<ChatPage />} />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to={isAuthenticated ? '/' : '/auth/login'} replace />} />
      </Routes>
    </WorkspaceProvider>
  );
}
